import {
  EventEmitter,
  forwardRef,
  Inject,
  Injectable,
  OnDestroy,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBar,
  MatSnackBarConfig,
} from '@angular/material/snack-bar';
import { getError, snapshot } from '@principle-theorem/shared';
import {
  browserPopupRedirectResolver,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  GithubAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  TwitterAuthProvider,
  updateProfile,
  User,
  UserCredential,
  UserInfo,
} from 'firebase/auth';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AccountIcon, ICredentials } from './auth-interfaces';
import {
  INgAuthConfig,
  NG_AUTH_CONFIG_TOKEN,
} from './ngx-auth-firebaseui-old/module-config';
import { AuthService } from './auth.service';
import { isError } from 'lodash';

export const facebookAuthProvider = new FacebookAuthProvider();
export const googleAuthProvider = new GoogleAuthProvider();
export const appleAuthProvider = new OAuthProvider('apple.com');
export const twitterAuthProvider = new TwitterAuthProvider();
export const githubAuthProvider = new GithubAuthProvider();
export const microsoftAuthProvider = new OAuthProvider('microsoft.com');
export const yahooAuthProvider = new OAuthProvider('yahoo.com');

export enum AuthProvider {
  ALL = 'all',
  ANONYMOUS = 'anonymous',
  EmailAndPassword = 'firebase',
  Google = 'google',
  Apple = 'apple',
  Facebook = 'facebook',
  Twitter = 'twitter',
  Github = 'github',
  Microsoft = 'microsoft',
  Yahoo = 'yahoo',
  PhoneNumber = 'phoneNumber',
}

export interface ISignInProcess {
  onSuccessEmitter: EventEmitter<UserCredential | User>;
  onErrorEmitter: EventEmitter<Error>;

  signInWith(provider: AuthProvider, credentials?: ICredentials): Promise<void>;
  resetPassword(email: string): Promise<void>;
}

export interface ISignUpProcess {
  signUp(name: string, credentials: ICredentials): Promise<void>;
}

@Injectable()
export class AuthProcessService
  implements ISignInProcess, ISignUpProcess, OnDestroy
{
  private _onDestroy$ = new Subject<void>();
  // Useful to know about auth state even between reloads.
  // Replace emailConfirmationSent and emailToConfirm.
  private _user$ = new BehaviorSubject<User | undefined>(undefined);

  onSuccessEmitter = new EventEmitter<UserCredential | User>();
  onErrorEmitter = new EventEmitter<Error>();

  get user$(): Observable<User | undefined> {
    return this._user$.asObservable();
  }

  /**
   * @deprecated access via user$ asynchronously instead
   */
  user?: User;

  messageOnAuthSuccess: string;
  messageOnAuthError: string;

  // Legacy field that is set to true after sign up.
  // Value is lost in case of reload. The idea here is to know if we just sent a verification email.
  emailConfirmationSent: boolean;
  // Legacy filed that contain the mail to confirm. Same lifecycle than emailConfirmationSent.
  emailToConfirm: string;

  constructor(
    protected _auth: Auth,
    protected _authService: AuthService,
    @Inject(forwardRef(() => NG_AUTH_CONFIG_TOKEN))
    public config: INgAuthConfig,
    private _snackBar: MatSnackBar,
    @Inject(MAT_SNACK_BAR_DEFAULT_OPTIONS)
    private _matSnackBarConfig: MatSnackBarConfig
  ) {
    this.listenToUserEvents();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  listenToUserEvents(): void {
    this._authService.authUser$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((user) => {
        this._user$.next((user as User) ?? undefined);
        this.user = (user as User) ?? undefined;
      });
  }

  /**
   * Reset the password of the ngx-auth-firebaseui-user via email
   *
   * @param email - the email to reset
   */
  public async resetPassword(email: string): Promise<void> {
    try {
      // eslint-disable-next-line no-console
      console.log('Password reset email sent');
      return await sendPasswordResetEmail(this._auth, email);
    } catch (error) {
      if (isError(error)) {
        return this.notifyError(error);
      }
    }
  }

  /**
   * General sign in mechanism to authenticate the users with a firebase project
   * using a traditional way, via username and password or by using an authentication provider
   * like google, facebook, twitter and github
   *
   * @param provider - the provider to authenticate with (google, facebook, twitter, github)
   * @param credentials optional email and password
   */
  public async signInWith(
    provider: AuthProvider,
    credentials?: ICredentials
  ): Promise<void> {
    try {
      const auth = this._auth;
      switch (provider) {
        case AuthProvider.ANONYMOUS:
          this.handleSuccess(await signInAnonymously(auth));
          break;

        case AuthProvider.EmailAndPassword:
          if (!credentials) {
            throw new Error(
              'Email and password are required for this provider'
            );
          }
          this.handleSuccess(
            await signInWithEmailAndPassword(
              auth,
              credentials.email,
              credentials.password
            )
          );
          break;

        case AuthProvider.Google:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              googleAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.Apple:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              appleAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.Facebook:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              facebookAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.Twitter:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              twitterAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.Github:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              githubAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.Microsoft:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              microsoftAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.Yahoo:
          this.handleSuccess(
            await signInWithPopup(
              auth,
              yahooAuthProvider,
              browserPopupRedirectResolver
            )
          );
          break;

        case AuthProvider.PhoneNumber:
          // coming soon - see feature/sms branch
          break;

        default:
          throw new Error(`${provider} is not available as auth provider`);
      }
    } catch (error) {
      if (isError(error)) {
        this.handleError(error);
      }
    }
  }

  /**
   * Sign up new users via email and password.
   * After that the ngx-auth-firebaseui-user should verify and confirm an email sent via the firebase
   *
   * @param displayName - the displayName if the new ngx-auth-firebaseui-user
   * @param credentials email and password
   * @returns -
   */
  public async signUp(
    displayName: string,
    credentials: ICredentials
  ): Promise<void> {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        this._auth,
        credentials.email,
        credentials.password
      );
      const user = userCredential.user;
      await this.updateProfile(displayName, user.photoURL ?? undefined);

      if (this.config.enableEmailVerification) {
        await sendEmailVerification(user);
      }

      // Legacy fields
      this.emailConfirmationSent = true;
      this.emailToConfirm = credentials.email;

      this.handleSuccess(userCredential);
    } catch (error) {
      if (isError(error)) {
        this.handleError(error);
      }
    }
  }

  async sendNewVerificationEmail(): Promise<void> {
    if (!this.user) {
      return Promise.reject(new Error('No signed in user'));
    }
    return sendEmailVerification(this.user);
  }

  async signOut(): Promise<void> {
    try {
      await this._auth.signOut();
    } catch (error) {
      if (isError(error)) {
        this.notifyError(error);
      }
    }
  }

  /**
   * Update the profile (name + photo url) of the authenticated ngx-auth-firebaseui-user in the
   * firebase authentication feature (not in firestore)
   *
   * @param name - the new name of the authenticated ngx-auth-firebaseui-user
   * @param photoURL - the new photo url of the authenticated ngx-auth-firebaseui-user
   * @returns -
   */
  public async updateProfile(name: string, photoURL?: string): Promise<void> {
    const user = await snapshot(this._authService.authUser$);
    if (!user) {
      throw new Error('No signed in user');
    }

    if (!photoURL) {
      return updateProfile(user, { displayName: name });
    }
    return updateProfile(user, { displayName: name, photoURL });
  }

  public parseUserInfo(user: User): UserInfo {
    return {
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      photoURL: user.photoURL,
      providerId:
        user.providerData.length > 0 ? user.providerData[0].providerId : '',
    };
  }

  public getUserPhotoUrl(): Observable<string | undefined> {
    return this._user$.pipe(
      map((user) => {
        if (!user) {
          return;
        } else if (user.photoURL) {
          return user.photoURL;
        } else if (user.emailVerified) {
          return this.getPhotoPath(AccountIcon.CHECK);
        } else if (user.isAnonymous) {
          return this.getPhotoPath(AccountIcon.OFF);
        } else {
          return this.getPhotoPath(AccountIcon.NONE);
        }
      })
    );
  }

  public getPhotoPath(image: AccountIcon): string {
    return `assets/user/${image}.svg`;
  }

  public signInWithPhoneNumber(): void {
    // todo: 3.1.18
  }

  handleSuccess(userCredential: UserCredential): void {
    if (this.config.useRawUserCredential) {
      this.onSuccessEmitter.next(userCredential);
    } else {
      this.onSuccessEmitter.next(userCredential.user);
    }

    if (this.config.toastMessageOnAuthSuccess) {
      const fallbackMessage = `Hello ${
        userCredential.user.displayName ? userCredential.user.displayName : ''
      }!`;
      this.showToast(this.messageOnAuthSuccess || fallbackMessage);
    }
  }

  handleError(error: Error): void {
    this.notifyError(error);
    // eslint-disable-next-line no-console
    console.error(error);
  }

  // Refresh user info. Can be useful for instance to get latest status regarding email verification.
  async reloadUserInfo(): Promise<void> {
    const user = await snapshot(this._user$);
    if (!user) {
      return;
    }
    await user.reload();
  }

  // Search for an error message.
  // Consumers of this library are given the possibility to provide a
  // function in case they want to instrument message based on error properties.
  getMessageOnAuthError(error: unknown): string {
    // eslint-disable-next-line no-bitwise
    return (
      getError(error) || 'Sorry, something went wrong. Please retry later.'
    );
  }

  // Show a toast using current snackbar config. If message is empty, no toast is displayed allowing to opt-out when needed.
  // Default MatSnackBarConfig has no duration, meaning it stays visible forever.
  // If that's the case, an action button is added to allow the end-user to dismiss the toast.
  showToast(message: string): void {
    if (!message) {
      return;
    }

    this._snackBar.open(
      message,
      this._matSnackBarConfig.duration ? undefined : 'OK'
    );
  }

  showErrorToast(error: unknown): void {
    if (this.config.toastMessageOnAuthError) {
      this.showToast(this.getMessageOnAuthError(error));
    }
  }

  notifyError(error: Error): void {
    this.onErrorEmitter.emit(error);
    this.showErrorToast(error);
  }
}
